/**
 * Anon-session carry-token persistence for storefront app-proxy traffic.
 *
 * Shopify strips `Set-Cookie` (and inbound `Cookie`) on the shop domain, so the
 * `fg_anon` cookie never round-trips. Without a stable session the proxy mints a
 * fresh empty `AnonymousSession` on every request — breaking guest reads, plan
 * status polls, rate-limit buckets, and CSRF on mutations. Same problem the
 * preview grant solves; same shape of fix.
 *
 * `/api/customer/context` returns `anonSessionCarry` (the signed cookie wire
 * value) ONLY when it mints a session. We stash it and replay it on every API
 * call via `X-FG-Anon-Session`.
 *
 * Security: the carry token is the cookie's 256-bit secret + HMAC — NOT the
 * session id (which is published in the context response and must never be used
 * as a credential). It lives in localStorage: sessionStorage is just as readable
 * by page scripts, so the exposure is unchanged, and the server's 30-day sliding
 * TTL + HMAC verification still bound it. The gain is identity: the same browser
 * is the same guest across tabs and days (per-tab carries were the biggest
 * generator of orphaned guest sessions). Every storage access is try/caught;
 * when storage throws (private mode, quota, disabled) the carry falls back to
 * memory for the page's lifetime.
 */

const STORAGE_KEY = "fg.anonSessionCarry";

/** Page-lifetime mirror, read only once storage has thrown. */
let memoryCarry: string | null = null;
let storageFailed = false;
let legacyMigrated = false;

/**
 * One-time move of a pre-localStorage carry (per-tab sessionStorage, same key)
 * so a guest mid-session isn't dropped onto a fresh session by the deploy. A
 * carry already in localStorage wins — it is shared with other open tabs, and
 * overwriting it would flip them onto this tab's session.
 */
function migrateLegacyCarry(): void {
  if (legacyMigrated) return;
  legacyMigrated = true;
  let legacy: string | undefined;
  try {
    legacy = sessionStorage.getItem(STORAGE_KEY)?.trim();
    if (!legacy) return;
    if (!localStorage.getItem(STORAGE_KEY)?.trim()) localStorage.setItem(STORAGE_KEY, legacy);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unusable — keep the legacy carry in memory for this page.
    if (legacy) {
      memoryCarry = legacy;
      storageFailed = true;
    }
  }
}

/**
 * Persist a freshly-issued carry token. No-ops on an absent/empty value so a
 * non-mint context response (which omits `anonSessionCarry`) never wipes the
 * credential we already hold. Updates in place when a *new* token arrives (a
 * re-mint after the prior session expired).
 */
export function storeAnonSessionCarry(carry: string | null | undefined): void {
  if (typeof window === "undefined") return;
  if (!carry || !carry.trim()) return;
  memoryCarry = carry.trim();
  try {
    localStorage.setItem(STORAGE_KEY, memoryCarry);
  } catch {
    storageFailed = true;
  }
}

/**
 * The carry to send. Read from localStorage on every call (not cached) so a
 * carry another tab stored — e.g. after a resume link rotated the session — is
 * picked up by this tab's very next request.
 */
export function readStoredAnonSessionCarry(): string | null {
  if (typeof window === "undefined") return null;
  migrateLegacyCarry();
  if (!storageFailed) {
    try {
      return localStorage.getItem(STORAGE_KEY)?.trim() || null;
    } catch {
      storageFailed = true;
    }
  }
  return memoryCarry;
}

/**
 * Drop the carry token. Called when the context resolves to a signed-in customer
 * (the anon session has been merged server-side, so the credential is spent).
 */
export function clearAnonSessionCarry(): void {
  if (typeof window === "undefined") return;
  memoryCarry = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Notify when ANOTHER tab stores a different carry (the `storage` event never
 * fires in the writing tab). Removals and same-value writes are ignored. The
 * new value is mirrored into memory so the in-memory fallback adopts it too.
 */
export function onAnonSessionCarryChangedElsewhere(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || e.storageArea !== localStorage) return;
    const next = e.newValue?.trim();
    if (!next || next === e.oldValue?.trim()) return;
    memoryCarry = next;
    onChange();
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}

/* ─── promoted-session hint (Phase B / lane B2) ──────────────────────────
 * When `/api/customer/context` resolves an anon principal whose presented
 * credential names a session that has already been PROMOTED to an account
 * (server sets `promotedToAccount: true`), the shopper is anon on THIS device
 * but their plan/data lives on a Fuel Goods account. We stash a durable hint so
 * screens can render `<PromotedSessionBanner>` — "Your plan is saved to your
 * account, sign in to view it".
 *
 * localStorage (NOT sessionStorage): the hint should survive tab close so a
 * returning shopper still sees the nudge. It is NOT a credential — just a
 * timestamp — so it carries no auth exposure.
 *
 * Lifetime (§2.3): set when a context response carries `promotedToAccount`;
 * cleared on auth success (a customer context response), on user dismissal,
 * superseded by a newer promotion (a later `at` overwrites), and after a 14-day
 * TTL (a stale hint self-expires on read).
 */

const PROMOTED_HINT_KEY = "fg.promotedHint";

/** 14 days — after this a stored hint is treated as expired and ignored/cleared. */
export const PROMOTED_HINT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

type PromotedHint = { at: number };

/**
 * Record that this device's anon session resolves to a promoted account.
 * Overwrites any prior hint with a fresh timestamp (supersession — a newer
 * promotion resets the 14-day clock).
 */
export function storePromotedHint(now: number = Date.now()): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PROMOTED_HINT_KEY, JSON.stringify({ at: now } satisfies PromotedHint));
  } catch {
    /* ignore — private mode / blocked storage */
  }
}

/**
 * Read the live promoted hint, or `null` when absent / malformed / older than
 * the 14-day TTL. A stale hint is proactively cleared on read so it can't linger.
 */
export function readPromotedHint(now: number = Date.now()): PromotedHint | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(PROMOTED_HINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PromotedHint> | null;
    const at = parsed && typeof parsed.at === "number" ? parsed.at : null;
    if (at == null) {
      localStorage.removeItem(PROMOTED_HINT_KEY);
      return null;
    }
    if (now - at >= PROMOTED_HINT_TTL_MS) {
      localStorage.removeItem(PROMOTED_HINT_KEY);
      return null;
    }
    return { at };
  } catch {
    return null;
  }
}

/** `true` when a live (non-expired) promoted hint is stored. */
export function hasLivePromotedHint(now: number = Date.now()): boolean {
  return readPromotedHint(now) != null;
}

/**
 * Drop the promoted hint. Called on auth success (customer context), on user
 * dismissal of the banner, and internally when the TTL lapses.
 */
export function clearPromotedHint(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(PROMOTED_HINT_KEY);
  } catch {
    /* ignore */
  }
}

/* ─── resume-link handoff (drop-due reminder emails) ─────────────────────
 * `GET /api/journeys/resume/:token` adopts the reminder's anon session and
 * redirects here with `?fg_resume_carry=<carry>` — the Set-Cookie it also
 * sends doesn't survive the Shopify app proxy, so the carry param is the
 * transport that actually works on the storefront. The param is stashed as
 * PENDING (not adopted) at boot: AuthProvider promotes it to the real carry
 * only when the context resolves anon, and discards it for signed-in
 * customers — their journeys are reunited by account email instead, and
 * auto-adopting a link's session under a signed-in user would let a
 * link-sender inject their session into someone else's account.
 */

const PENDING_RESUME_KEY = "fg.pendingResumeCarry";

/**
 * Capture `?fg_resume_carry=` into the pending slot and scrub it from the URL
 * (it's a credential — it must not linger in the address bar / history).
 * Called once from main.tsx before React mounts.
 */
export function capturePendingResumeCarryFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const carry = url.searchParams.get("fg_resume_carry");
    if (!carry || !carry.trim()) return;
    sessionStorage.setItem(PENDING_RESUME_KEY, carry.trim());
    url.searchParams.delete("fg_resume_carry");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  } catch {
    /* ignore — private mode / blocked storage */
  }
}

/** Consume (read + remove) the pending resume carry. */
export function takePendingResumeCarry(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const carry = sessionStorage.getItem(PENDING_RESUME_KEY);
    sessionStorage.removeItem(PENDING_RESUME_KEY);
    return carry?.trim() ? carry.trim() : null;
  } catch {
    return null;
  }
}
